import { StarIcon } from "@heroicons/react/24/solid";
import { useEffect, useState, Fragment } from "react";

import {
  BriefcaseIcon,
  CurrencyDollarIcon,
  MapPinIcon as LocationMarkerIcon,
} from "@heroicons/react/24/solid";
import get from "lodash/get";
import dayjs from "dayjs";
import { Tab } from "@headlessui/react";
import Image from "next/legacy/image";
import Link from "next/link";

import ProjectImageSlider from "../../components/project-image-slider";
import Meta from "../../components/meta";
import BlockContent from "../../components/block-content";
import {
  getDeliveryMethodLabel,
  getProjectCategoryLabel,
} from "../../lib/helpers";
import {
  blurDataURL,
  CMS_URL,
  TwitterIcon,
  LinkedInIcon,
  FacebookIcon,
} from "../../lib/constants";
import { imageBuilder } from "../../lib/sanity";
import { getProject, getAllProjectsWithSlug } from "../../lib/api";

const getDetails = (data) => {
  const {
    location,
    value,
    method,
    categories,
    client,
    architect,
    completedOn,
  } = data || {};
  const details = [];
  if (client) {
    details.push({
      title: "Client",
      value: client,
    });
  }
  if (architect) {
    details.push({
      title: "Architect",
      value: architect,
    });
  }
  if (completedOn) {
    details.push({
      title: "Completed",
      value: dayjs(completedOn).format("MMMM YYYY"),
    });
  }
  if (location) {
    details.push({
      title: "Location",
      value: location,
      icon: LocationMarkerIcon,
      showInHeader: true,
    });
  }
  if (value) {
    details.push({
      title: "Project Value",
      value,
      icon: CurrencyDollarIcon,
      showInHeader: true,
    });
  }
  if (method) {
    details.push({
      title: "Delivery Method",
      value: getDeliveryMethodLabel(method),
      icon: BriefcaseIcon,
      showInHeader: true,
    });
  }
  if (categories && categories.length) {
    const categoryLabels = categories.map((cat) =>
      getProjectCategoryLabel(cat)
    );
    details.push({
      title: "Categories",
      value: categoryLabels.join(", "),
      icon: StarIcon,
      showInHeader: true,
    });
  }
  return details;
};

const SocialShare = ({ data, className }) => {
  const url = encodeURIComponent(
    `${CMS_URL}/projects/${data.slug && data.slug.current}`
  );
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}&t=${data.title}`;
  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
  const twitterUrl = `https://twitter.com/share?url=${url}&text=${data.title}`;

  return (
    <div className={`${className} border-t border-gray-200 mt-10 pt-10`}>
      <h3 className="text-sm font-medium text-gray-900">Share</h3>
      <ul role="list" className="flex items-center space-x-6 mt-4">
        <li>
          <a
            target="_blank"
            rel="noreferrer"
            href={facebookUrl}
            className="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Share on Facebook</span>
            <FacebookIcon className="w-6 h-6" />
          </a>
        </li>
        <li>
          <a
            target="_blank"
            rel="noreferrer"
            href={linkedInUrl}
            className="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Share on LinkedIn</span>
            <LinkedInIcon className="w-6 h-6" />
          </a>
        </li>
        <li>
          <a
            target="_blank"
            rel="noreferrer"
            href={twitterUrl}
            className="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Share on Twitter</span>
            <TwitterIcon className="w-6 h-6" />
          </a>
        </li>
      </ul>
    </div>
  );
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const Timelapse = ({ url }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!data);

  // Fetch embed
  useEffect(() => {
    if (data && data.url === url) {
      setLoading(false);
      return;
    }

    setLoading(true);
    fetch(
      `https://us-central1-clark-sullivan-intranet.cloudfunctions.net/sharedItem?url=${url}`
    )
      .then((response) => response.json())
      .then((data) => {
        setLoading(false);
        if (data.error) {
          console.error(data.error);
          return;
        }
        const obj = { ...data, url };
        setData(obj);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  const embedUrl = get(data, "file.expiring_embed_link.url");
  return (
    <div className="aspect-w-16 aspect-h-9 relaative rounded-lg overflow-hidden shadow">
      {loading ? (
        <div className="w-full inset-0 rounded-lg shadow bg-black items-center justify-center flex text-white">
          Loading...
        </div>
      ) : embedUrl ? (
        <iframe
          allowFullScreen
          className="w-full inset-0 h-[calc(100%+48px)] -mt-[48px] bg-black rounded-lg"
          src={`${embedUrl}`}
        />
      ) : null}
    </div>
  );
};

const ProjectImage = ({
  image,
  className = "rounded-lg",
  imgClassName = "",
  children,
  onClick = () => {},
  thumbnail = false,
}) => {
  if (!image) return null;

  const desktopUrl = imageBuilder(image)
    .width(1280)
    .height(960)
    .crop("entropy")
    .url();

  const thumbnailUrl = imageBuilder(image)
    .width(73)
    .height(55)
    .dpr(2)
    .crop("entropy")
    .url();

  const url = thumbnail ? thumbnailUrl : desktopUrl;
  return (
    <div
      className={`aspect-w-4 aspect-h-3 ${className} bg-gray-100 overflow-hidden`}
      onClick={onClick}
    >
      <Image
        draggable={false}
        className={`object-center object-cover ${imgClassName}`}
        alt=""
        layout="fill"
        priority={!thumbnail}
        placeholder="blur"
        blurDataURL={blurDataURL}
        src={url}
      />
      {children}
    </div>
  );
};

export default function Project({ data = {} }) {
  const { images, timelapse } = data || {};
  const breadcrumbs = [
    { id: 1, name: "Projects", href: "/projects" },
    {
      id: 2,
      name: getDeliveryMethodLabel(data.method),
      href: `/projects?methods=${data.method}`,
    },
  ];

  const details = getDetails(data);

  return (
    <>
      <Meta
        path={`/projects/${data.slug}`}
        title={data.title}
        description={data.description}
        image={get(images, "[0]")}
      />

      <div className="bg-white">
        <div className="mx-auto !pt-10 py-16 px-4 sm:py-24 sm:px-6 lg:max-w-7xl lg:px-8">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-8 hidden md:block">
            <ol role="list" className="flex items-center space-x-4">
              {breadcrumbs.map((breadcrumb) => (
                <li key={breadcrumb.id}>
                  <div className="flex items-center">
                    <Link
                      href={breadcrumb.href}
                      className="mr-4 text-sm font-medium text-gray-900"
                    >
                      {breadcrumb.name}
                    </Link>
                    <svg
                      viewBox="0 0 6 20"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      className="h-5 w-auto text-gray-300"
                    >
                      <path
                        d="M4.878 4.34H3.551L.27 16.532h1.327l3.281-12.19z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                </li>
              ))}
              <li className="text-sm">
                <Link
                  href={`/projects/${data.slug && data.slug.current}`}
                  aria-current="page"
                  className="font-medium text-gray-500 hover:text-gray-600"
                >
                  {data.title}
                </Link>
              </li>
            </ol>
          </nav>

          <div className="flex flex-col-reverse md:hidden mb-6">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
                {data.title}
              </h1>

              <div className="mt-2">
                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:c-gap sm:c-gap-x-4 sm:c-gap-y-2">
                  {details
                    .filter((i) => !!i.showInHeader)
                    .map((i) => {
                      return (
                        <div
                          key={i.value}
                          className="mt-2 flex items-center text-sm text-gray-500"
                        >
                          <i.icon
                            className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                            aria-hidden="true"
                          />
                          {i.value}
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>

          {/* Product */}
          <div className="lg:grid lg:grid-rows-1 lg:grid-cols-7 lg:gap-x-8 lg:gap-y-6 xl:gap-x-16">
            {/* Product image */}
            <div className="lg:row-end-1 lg:col-span-4">
              {images && images.length ? (
                <ProjectImageSlider images={images} />
              ) : (
                <ProjectImage image={data.image} />
              )}
            </div>

            {/* Product details */}
            <div className="max-w-2xl mx-auto mt-10 sm:mt-12 lg:max-w-none lg:mt-0 lg:row-end-2 lg:row-span-2 lg:col-span-3">
              {/* Title and details desktop */}
              <div className="flex-col-reverse hidden md:flex">
                <div>
                  <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
                    {data.title}
                  </h1>

                  <div className="mt-2">
                    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:c-gap sm:c-gap-x-4 sm:c-gap-y-2">
                      {details
                        .filter((i) => !!i.showInHeader)
                        .map((i) => {
                          return (
                            <div
                              key={i.value}
                              className="mt-2 flex items-center text-sm text-gray-500"
                            >
                              <i.icon
                                className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                                aria-hidden="true"
                              />
                              {i.value}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <BlockContent
                  className="text-gray-500 prose"
                  content={data.description}
                />
              </div>

              <SocialShare className="hidden sm:block" data={data} />
            </div>

            <div className="w-full max-w-2xl mx-auto mt-6 sm:mt-16 lg:max-w-none lg:mt-0 lg:col-span-4">
              <Tab.Group as="div" className="outline-none">
                <div className="border-b border-gray-200">
                  <Tab.List className="-mb-px flex space-x-8 outline-none">
                    <Tab
                      className={({ selected }) =>
                        classNames(
                          selected
                            ? "border-accent-600 text-accent-600"
                            : "border-transparent text-gray-700 hover:text-gray-800 hover:border-gray-300",
                          "whitespace-nowrap py-6 border-b-2 font-medium text-sm transition outline-none"
                        )
                      }
                    >
                      Details
                    </Tab>

                    {timelapse ? (
                      <Tab
                        className={({ selected }) =>
                          classNames(
                            selected
                              ? "border-accent-600 text-accent-600"
                              : "border-transparent text-gray-700 hover:text-gray-800 hover:border-gray-300",
                            "whitespace-nowrap py-6 border-b-2 font-medium text-sm transition"
                          )
                        }
                      >
                        Timelapse
                      </Tab>
                    ) : null}
                  </Tab.List>
                </div>

                <Tab.Panels as={Fragment}>
                  <Tab.Panel className="outline-none">
                    <div>
                      <dl className="sm:divide-y sm:divide-gray-200">
                        {details.map((i) => {
                          return (
                            <div
                              key={i.value}
                              className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4"
                            >
                              <dt className="text-sm font-medium text-gray-500">
                                {i.title}
                              </dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {i.value}
                              </dd>
                            </div>
                          );
                        })}
                      </dl>
                    </div>
                  </Tab.Panel>

                  {timelapse ? (
                    <Tab.Panel className="pt-6 outline-none">
                      <h3 className="sr-only">Timelapse</h3>
                      <Timelapse key={timelapse} url={timelapse} />
                    </Tab.Panel>
                  ) : null}
                </Tab.Panels>
              </Tab.Group>
            </div>

            <SocialShare className="sm:hidden" data={data} />
          </div>
        </div>
      </div>
    </>
  );
}

export async function getStaticProps({ params, preview = false }) {
  const data = await getProject(params.slug, preview);
  return {
    props: { data },
    revalidate: 1,
  };
}

export async function getStaticPaths() {
  const allProjects = await getAllProjectsWithSlug();
  return {
    paths:
      allProjects?.map((project) => ({
        params: {
          slug: project.slug,
        },
      })) || [],
    fallback: "blocking",
  };
}
